import { readonly, ref } from 'vue'
import { storeToRefs } from 'pinia'
import * as Sentry from '@sentry/vue'
import type { AudioDeviceRole, AudioSettings, PreferredAudioDevices } from '@/types/types'
import { useDeviceStore } from '@/stores/device'
import { useUserStore } from '@/stores/user'

// Module-level singletons so every consumer (App.vue's volume meters / applying
// devices on call connect, and the NavBar menu's device pickers) shares the same
// audio settings rather than getting an isolated copy per component.
//
// The user's choice and the device in use are kept apart on purpose. A single
// enumeration can't tell "the headset is gone" from "the list is momentarily
// incomplete" — a hot-plugged headset drops out for a moment, and a Twilio
// device being recreated reports fewer devices than are really there — so a
// choice stored as one value gets overwritten by a bad snapshot and never comes
// back.
const audio = ref<AudioSettings>({
  input: [],
  output: [],
  microphoneDevice: null,
  speakerDevice: null,
  inputVolume: 0,
  outputVolume: 0,
})

// Preferences belong to a rep, not to a browser: reps share machines, so the
// key is scoped to the user id. Logging out doesn't hand your headset to the
// next person, and getting yours back doesn't depend on the previous session
// having ended cleanly.
const storageKey = (userId: number) => `callingly.audioDevices.${userId}`

const noPreferences = (): PreferredAudioDevices => ({
  microphone: null,
  speaker: null,
})

// Preferences outlive the tab: a rep picks their headset once, not once per
// refresh. deviceIds are stable for an origin that holds microphone permission,
// which is exactly the state the app runs in; if permission is ever revoked the
// ids stop matching and resolution falls back on its own.
//
// Storage can be unavailable (Safari private mode, storage disabled) or hold
// whatever an older version of the app wrote, so anything unreadable is treated
// as "no preference yet" rather than allowed to break startup. Reading by the
// roles we know about also means the separate `ringtone` older versions saved
// is ignored rather than needing a migration — it's the speaker's job now.
const loadPreferences = (userId: number): PreferredAudioDevices => {
  const preferences = noPreferences()

  try {
    const stored: unknown = JSON.parse(window.localStorage.getItem(storageKey(userId)) ?? 'null')

    if (stored && typeof stored === 'object') {
      Object.keys(preferences).forEach((role) => {
        const value = (stored as Record<string, unknown>)[role]

        if (typeof value === 'string') {
          preferences[role as AudioDeviceRole] = value
        }
      })
    }
  } catch {
    // Unreadable or not JSON — fall through with nothing preferred.
  }

  return preferences
}

const savePreferences = (userId: number, preferences: PreferredAudioDevices) => {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(preferences))
  } catch {
    // Storage full or unavailable; the pick still holds for this session.
  }
}

// What the user picked. Only selectAudioDevice() writes this; enumeration never
// touches it, so a preference survives the device disappearing and takes effect
// again on its own once it comes back.
const preferred = ref<PreferredAudioDevices>(noPreferences())

// Which user the in-memory preferences were loaded for. `undefined` means they
// haven't been loaded at all yet, which is distinct from "loaded, nobody is
// logged in".
let loadedFor: number | null | undefined = undefined

// One stale-device rejection is the expected race. The same role failing that
// way call after call is not: resolveDeviceId deliberately keeps a preference
// when Twilio reports no devices at all, so a saved id that is gone for good
// gets reapplied on every call and the rep takes them on stale or absent audio.
// Counting the consecutive failures per role keeps the race a breadcrumb and
// still reports the permanent case — once, not once per call.
const staleDeviceFailures = new Map<string, number>()
const reportStaleDeviceAfter = 3

// Resolve one role's preference against the devices visible right now.
//
// An empty list is not evidence that anything went away — it means we can't see
// the hardware at all (no Twilio device yet, or one being recreated) — so the
// preference stands. Otherwise the preference wins if it's present, and we fall
// back only while it genuinely isn't. Fallbacks are themselves checked against
// the list, since Twilio's currently-set device can be the stale one.
const resolveDeviceId = (
  preference: string | null,
  available: Array<MediaDeviceInfo>,
  ...fallbacks: Array<string | undefined>
): string => {
  if (!available.length) {
    return preference ?? fallbacks.find(Boolean) ?? 'default'
  }

  if (preference && available.some((dev) => dev.deviceId === preference)) {
    return preference
  }

  const usable = fallbacks.find((id) => id && available.some((dev) => dev.deviceId === id))

  return usable ?? available[0]?.deviceId ?? 'default'
}

export function useAudioDevices() {
  // The device Twilio currently has set for a role, which is the best guess at
  // what to use when the user has never picked anything.
  // Optional chaining all the way down: this now runs while a call is being
  // answered, and a half-built Twilio device isn't worth dropping a call over.
  const currentTwilioSpeaker = () => {
    const { device } = storeToRefs(useDeviceStore())

    return device.value?.audio?.speakerDevices?.get?.().values().next().value?.deviceId
  }

  // Point the in-memory preferences at whoever is logged in now. The module is
  // imported long before the user is fetched, and a browser can outlive a
  // session, so this is checked on the way past rather than read once at
  // startup: a new user starts from nothing until they load their own.
  const syncPreferencesToUser = (): number | null => {
    const { user } = storeToRefs(useUserStore())
    const userId = user.value?.id ?? null

    if (userId !== loadedFor) {
      loadedFor = userId
      preferred.value = userId === null ? noPreferences() : loadPreferences(userId)
    }

    return userId
  }

  // Recompute the devices in use from the preferences plus the current lists.
  // Cheap and idempotent, so it runs after anything that could change either.
  const resolveAudioDevices = () => {
    syncPreferencesToUser()

    audio.value.microphoneDevice = resolveDeviceId(preferred.value.microphone, audio.value.input)

    audio.value.speakerDevice = resolveDeviceId(
      preferred.value.speaker,
      audio.value.output,
      currentTwilioSpeaker(),
    )
  }

  const updateAudioDevicesList = () => {
    const { device } = storeToRefs(useDeviceStore())

    audio.value.input = []
    audio.value.output = []

    device.value?.audio?.availableInputDevices?.forEach((dev: MediaDeviceInfo) => {
      audio.value.input.push(dev)
    })

    device.value?.audio?.availableOutputDevices?.forEach((dev: MediaDeviceInfo) => {
      audio.value.output.push(dev)
    })

    // Preferences are untouched here — this only re-derives what's in use, so a
    // device that reappears in this snapshot becomes active again by itself.
    resolveAudioDevices()
  }

  // The user picked a device from one of the menu's pickers. Pass null to clear
  // the preference and go back to whatever is available.
  const selectAudioDevice = (role: AudioDeviceRole, deviceId: string | null) => {
    const userId = syncPreferencesToUser()
    preferred.value[role] = deviceId

    // Nobody to save against before login; the pick still holds for this
    // session, and the pickers only exist once there's a user anyway.
    if (userId !== null) {
      savePreferences(userId, preferred.value)
    }

    resolveAudioDevices()
  }

  // Applying a device can reject (or throw) when the resolved id isn't one
  // Twilio actually holds — the saved/enumerated device vanished in the moment
  // between resolve and set, or the browser doesn't expose a 'default' id, so
  // Twilio raises "Device not found: default". A single one of those is an
  // expected hardware race, not a bug: enumeration re-resolves on its own and
  // the next attempt lands on a real device. Swallow *that* to a breadcrumb
  // rather than letting an unhandled rejection become a Sentry issue per rep
  // machine.
  const staleDevicePattern = /Device not found/i

  const applyDevice = (role: string, apply: () => Promise<unknown> | undefined) => {
    const handle = (error: unknown) => {
      const message = error instanceof Error ? error.message : String(error)
      const isStale = staleDevicePattern.test(message)
      const consecutive = isStale ? (staleDeviceFailures.get(role) ?? 0) + 1 : 0

      if (isStale) {
        staleDeviceFailures.set(role, consecutive)
      }

      Sentry.addBreadcrumb({
        category: 'audio',
        level: 'warning',
        message: `Could not set ${role} device`,
        data: { error: message, consecutive },
      })

      // Anything that isn't the stale-device race — the browser refusing output
      // selection, a device that persistently won't open — leaves the rep on the
      // wrong audio for the call and has to be reported, not just breadcrumbed.
      // (The getUserMedia hardware failures are dropped by ignoredErrors.)
      //
      // A stale-device failure reports too once it has stopped looking like a
      // race. Only on the crossing, so a rep whose saved device is gone for good
      // produces one event rather than one per call; the next success clears it.
      if (!isStale || consecutive === reportStaleDeviceAfter) {
        Sentry.captureException(error)
      }
    }

    const succeeded = () => staleDeviceFailures.delete(role)

    try {
      Promise.resolve(apply()).then(succeeded, handle)
    } catch (error) {
      handle(error)
    }
  }

  const setAudioDevices = () => {
    const { device } = storeToRefs(useDeviceStore())

    // Re-enumerate first: this runs as a call is being answered, so resolve
    // against the hardware as it is at that moment, not as it was when the menu
    // was last opened.
    updateAudioDevicesList()

    applyDevice('input', () =>
      device.value?.audio?.setInputDevice(audio.value.microphoneDevice ?? 'default'),
    )

    // Call audio and the incoming ringtone are separate sinks in Twilio, but
    // one choice to the rep: whatever they hear the call through is where it
    // rings.
    const speakerDevice = audio.value.speakerDevice
    if (speakerDevice) {
      applyDevice('speaker', () => device.value?.audio?.speakerDevices.set(speakerDevice))
      applyDevice('ringtone', () => device.value?.audio?.ringtoneDevices.set(speakerDevice))
    }
  }

  return {
    audio,
    // Exposed for inspection only — go through selectAudioDevice() to change it.
    preferredAudioDevices: readonly(preferred),
    updateAudioDevicesList,
    selectAudioDevice,
    setAudioDevices,
  }
}
